import { normalizeNewsletterStatus, newsletterStatusKey, newsletterStatusLabel } from '../../lib/newsletterStatus';

export type CalendarItemKind = 'content' | 'connection' | 'transcript';

export { newsletterStatusKey, newsletterStatusLabel };

export type CalendarItem = {
  id: string;
  entityId: string;
  kind: CalendarItemKind;
  title: string;
  subtitle: string | null;
  status: string | null;
  dateKey: string;
  /** Raw timestamptz/date for display in preview modals. */
  dateAt: string | null;
  channels?: unknown;
};

export const KIND_SORT: Record<CalendarItemKind, number> = {
  content: 0,
  transcript: 1,
  connection: 2,
};

/** Badge colors aligned with newsletter detail header pills. */
export function statusPill(statusRaw: string | null): string {
  const s = newsletterStatusKey(statusRaw);
  if (s === 'published' || s === 'sent')
    return 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-200';
  if (s === 'ready_to_publish' || s === 'queued')
    return 'bg-sky-100 text-sky-900 dark:bg-sky-900/30 dark:text-sky-200';
  if (s === 'scheduled')
    return 'bg-violet-100 text-violet-900 dark:bg-violet-900/30 dark:text-violet-200';
  if (s === 'on_hold')
    return 'bg-amber-100 text-amber-900 dark:bg-amber-900/30 dark:text-amber-200';
  if (s === 'draft') return 'bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-200';
  if (s === 'error') return 'bg-rose-100 text-rose-900 dark:bg-rose-900/30 dark:text-rose-200';
  return 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-200';
}

/** Left accent + tint on calendar day cells for newsletter content. */
export function contentCalendarCardClass(statusRaw: string | null): string {
  const s = newsletterStatusKey(statusRaw);
  if (s === 'published')
    return 'border-l-[3px] border-l-emerald-500 bg-emerald-50/60 dark:bg-emerald-950/25';
  if (s === 'ready_to_publish')
    return 'border-l-[3px] border-l-sky-500 bg-sky-50/50 dark:bg-sky-950/25';
  if (s === 'scheduled')
    return 'border-l-[3px] border-l-violet-500 bg-violet-50/50 dark:bg-violet-950/25';
  if (s === 'on_hold')
    return 'border-l-[3px] border-l-amber-500 bg-amber-50/50 dark:bg-amber-950/20';
  if (s === 'draft')
    return 'border-l-[3px] border-l-slate-400 bg-slate-50/60 dark:bg-slate-900/35';
  return 'border-l-[3px] border-l-gray-300 dark:border-l-gray-600';
}

export function newsletterStatusCalendarHint(statusRaw: string | null): string | null {
  const s = newsletterStatusKey(statusRaw);
  if (s === 'draft') return 'Draft — send date is planning only until status is Scheduled.';
  if (s === 'scheduled') return 'Scheduled — committed; recipients queue ~24h before send.';
  if (s === 'ready_to_publish') return 'Ready to Publish — recipients queued; sends at planned time.';
  if (s === 'on_hold') return 'On hold — nothing queues or sends.';
  if (s === 'published') return 'Published — send finished.';
  return null;
}

export const NEWSLETTER_STATUS_LEGEND: { label: string; hint: string }[] = [
  { label: 'Draft', hint: 'Date only — not committed' },
  { label: 'Scheduled', hint: 'Committed — auto-queue before send' },
  { label: 'Ready to Publish', hint: 'Recipients queued' },
  { label: 'On Hold', hint: 'Paused' },
  { label: 'Published', hint: 'Done' },
];

export function contentCalendarLegendDotClass(statusLabel: string): string {
  const s = normalizeNewsletterStatus(statusLabel);
  if (s === 'published') return 'bg-emerald-500';
  if (s === 'ready_to_publish') return 'bg-sky-500';
  if (s === 'scheduled') return 'bg-violet-500';
  if (s === 'on_hold') return 'bg-amber-500';
  if (s === 'draft') return 'bg-slate-400';
  return 'bg-gray-400';
}

export function kindPill(kind: CalendarItemKind): string {
  if (kind === 'content') return 'bg-emerald-50 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-200';
  if (kind === 'connection') return 'bg-blue-50 text-blue-700 dark:bg-blue-900/30 dark:text-blue-200';
  return 'bg-violet-50 text-violet-700 dark:bg-violet-900/30 dark:text-violet-200';
}

export function kindLabel(kind: CalendarItemKind): string {
  if (kind === 'content') return 'Content';
  if (kind === 'connection') return 'Connection';
  return 'Transcript';
}

export function formatCalendarDate(val: string | null | undefined): string {
  if (!val) return '—';
  try {
    return new Date(val).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' });
  } catch {
    return val;
  }
}

export function formatDayHeading(dateKey: string): string {
  const [y, m, d] = dateKey.split('-').map(Number);
  if (!y || !m || !d) return dateKey;
  const dt = new Date(y, m - 1, d);
  return dt.toLocaleDateString(undefined, { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' });
}

export function calendarItemHref(item: CalendarItem): string {
  if (item.kind === 'content') return `/outgoing-newsletters/${item.entityId}/content`;
  if (item.kind === 'connection') return `/subscribers/${item.entityId}`;
  return `/transcripts/${item.entityId}`;
}

export function openItemLabel(kind: CalendarItemKind): string {
  if (kind === 'content') return 'Open content';
  if (kind === 'connection') return 'View connection';
  return 'Open transcript';
}

export function dateFieldLabel(kind: CalendarItemKind): string {
  if (kind === 'content') return 'Scheduled for';
  if (kind === 'connection') return 'Last contacted';
  return 'Meeting date';
}
